import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { XMarkIcon } from '@heroicons/react/24/solid';
import { QRCodeSVG } from 'qrcode.react';

import { useEnsureCsrfToken } from '~/context/CsrfContext';
import type { MobileAppLoginLink } from '~/routes/__actions/community_mobile_app_login_link';
import type { NectarPageBlock } from '~/utils/nectarPageBlock';

import { Dialog } from './ui/Dialog';
import { PillButton } from './ui/PillButton';

const LOGIN_LINK_ACTION = '/community_mobile_app_login_link';
// Refresh this far ahead of expiry so a scan always lands on a live token.
const REFRESH_LEAD_MS = 30_000;
const RETRY_BACKOFF_MS = 5_000;

interface CommunityMobileAppQrDialogProps {
  isOpen: boolean;
  onClose: () => void;
  pageBlock: NectarPageBlock;
}

type QrStatus = 'loading' | 'ready' | 'error';

export function CommunityMobileAppQrDialog({ isOpen, onClose, pageBlock }: CommunityMobileAppQrDialogProps) {
  const { t } = useTranslation();
  const ensureCsrfToken = useEnsureCsrfToken();
  const [loginUrl, setLoginUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<QrStatus>('loading');
  const expiresAtRef = useRef<number | null>(null);
  // Lets the retry button re-run the open effect's fetch/schedule loop without a
  // phantom dependency just to re-trigger the effect.
  const restartRef = useRef<(() => void) | null>(null);

  const stillValid = useCallback(
    () => expiresAtRef.current != null && expiresAtRef.current - REFRESH_LEAD_MS > Date.now(),
    [],
  );

  const fetchToken = useCallback(
    async (signal: AbortSignal): Promise<boolean> => {
      try {
        const csrfToken = await ensureCsrfToken();
        const res = await fetch(LOGIN_LINK_ACTION, {
          method: 'POST',
          signal,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          },
          body: new URLSearchParams({ page_block: pageBlock }),
        });
        if (!res.ok) throw new Error(`login link request failed: ${res.status}`);

        const data = (await res.json()) as MobileAppLoginLink;
        // A client-local deadline (not the server timestamp) so scheduling and stillValid
        // read the same clock and clock skew can't distort the refresh cadence.
        expiresAtRef.current = Date.now() + data.expires_in * 1000;
        setLoginUrl(data.login_url);
        setStatus('ready');
        return true;
      } catch {
        if (signal.aborted) return false;
        // Preserve a still-valid QR through transient failures; only surface retry when
        // there's nothing scannable left.
        if (!stillValid()) {
          setLoginUrl(null);
          setStatus('error');
        }
        return false;
      }
    },
    [ensureCsrfToken, pageBlock, stillValid],
  );

  useEffect(() => {
    if (!isOpen) return;

    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | null = null;

    const run = async () => {
      const ok = await fetchToken(controller.signal);
      if (controller.signal.aborted) return;

      if (ok && expiresAtRef.current != null) {
        const delay = Math.max(0, expiresAtRef.current - Date.now() - REFRESH_LEAD_MS);
        timer = setTimeout(run, delay);
      } else if (stillValid()) {
        // Background refresh failed but the current QR is still good — retry before expiry.
        timer = setTimeout(run, RETRY_BACKOFF_MS);
      }
    };

    const restart = () => {
      if (timer) clearTimeout(timer);
      setStatus('loading');
      void run();
    };
    restartRef.current = restart;

    setStatus('loading');
    void run();

    // A tab returning to the foreground with a stale QR refreshes immediately.
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !stillValid()) void run();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      controller.abort();
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      restartRef.current = null;
      setLoginUrl(null);
      setStatus('loading');
      expiresAtRef.current = null;
    };
  }, [isOpen, fetchToken, stillValid]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      className="w-full overflow-hidden rounded-lg bg-white shadow-lg sm:w-[460px]"
    >
      <div className="p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-xl font-bold">{t('community.mobile_app.qr_title')}</h2>
          <button type="button" aria-label={t('community.mobile_app.close')} className="shrink-0" onClick={onClose}>
            <XMarkIcon className="size-5 text-black/40" />
          </button>
        </div>
        <div className="mb-4 flex items-center justify-center rounded-lg bg-gray-100 p-8">
          <div className="rounded bg-white p-4">
            {status === 'ready' && loginUrl ? (
              <QRCodeSVG value={loginUrl} size={192} level="H" />
            ) : status === 'error' ? (
              <div className="flex size-48 flex-col items-center justify-center gap-3">
                <p className="text-center text-sm text-gray-600">{t('community.mobile_app.qr_error')}</p>
                <PillButton type="button" variant="primary" onClick={() => restartRef.current?.()}>
                  {t('community.mobile_app.qr_retry')}
                </PillButton>
              </div>
            ) : (
              <div className="size-48 animate-pulse rounded bg-gray-100" aria-hidden="true" />
            )}
          </div>
        </div>
        <p className="text-center text-sm text-gray-600">{t('community.mobile_app.qr_description')}</p>
        {status === 'ready' ? (
          <div className="mt-4 flex justify-center">
            <PillButton type="button" variant="secondary" onClick={() => restartRef.current?.()}>
              {t('community.mobile_app.qr_reload')}
            </PillButton>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
